import { Injectable, Type } from '@angular/core';
import { TextDisplayComponent } from '../displays/text-display.component';
import { NumberDisplayComponent } from '../displays/number-display.component';
import { DateDisplayComponent } from '../displays/date-display.component';
import { DateTimeDisplayComponent } from '../displays/date-time-display.component';
import { BooleanDisplayComponent } from '../displays/boolean-display.component';
import { SelectDisplayComponent } from '../displays/select-display.component';
import { RelationDisplayComponent } from '../displays/relation-display.component';
import { TextInputComponent } from '../inputs/text-input.component';
import { NumberInputComponent } from '../inputs/number-input.component';
import { DateInputComponent } from '../inputs/date-input.component';
import { DateTimeInputComponent } from '../inputs/date-time-input.component';
import { BooleanInputComponent } from '../inputs/boolean-input.component';
import { SelectInputComponent } from '../inputs/select-input.component';
import { RelationInputComponent } from '../inputs/relation-input.component';

export interface AttributeComponents {
  display: Type<unknown>;
  input: Type<unknown>;
}

/**
 * Partial pair for {@link AttributeComponentRegistry.registerForSystemObject}. Either half may
 * be omitted; missing halves fall back to the property-type lookup (typically the generic
 * relation renderer). Useful when a system object only needs a custom display (e.g. Tags chip
 * row) while reusing the generic input — or vice versa.
 */
export interface PartialAttributeComponents {
  display?: Type<unknown>;
  input?: Type<unknown>;
}

/**
 * How a system object's `createView` reports its result back to the host.
 *
 *  - `'self-persisted'` — the view internally POSTs the new entity (e.g. tags eagerly call
 *    `TagsApi.createTag`) and emits the saved entity (with id) on `save`. The host just reads
 *    the id and adds it to the parent's relation selection. Used when the entity is global
 *    (not a child of the parent being edited) — orphan risk is low/acceptable.
 *  - `'batched'` — the view emits raw form data on `save`. The host stages the data on a batch
 *    coordinator tied to the parent form's submit; a placeholder id is used until the batch
 *    resolves. Used when the entity only makes sense as a child of the parent (e.g. Location).
 *    Not exercised yet for TF-1510; reserved for when the second system-object bundle ships.
 */
export type SystemObjectCreateMode = 'self-persisted' | 'batched';

/**
 * Per-slug bundle of components and metadata for a built-in system object (e.g. Tags, Location).
 *
 * The registry holds these bundles by slug; the field-renderer consults the picker pair when
 * resolving a relation field that targets this slug, and the instance form's create flow
 * consults `createView` + `createMode` when the user clicks "+ Create" on such a relation.
 *
 * Bundles are deliberately a single data structure rather than separate registries per role:
 * Tags' picker, display, and create view are all parts of the same logical "Tags UI", and
 * keeping them together avoids the risk of partial registration (e.g. picker registered, create
 * view forgotten).
 */
export interface SystemObjectBundle {
  /** Multi-select picker rendered for `relation`/`record` fields targeting this slug. */
  pickerInput?: Type<unknown>;
  /** Read-only display rendered for `relation`/`record` fields targeting this slug. */
  pickerDisplay?: Type<unknown>;
  /**
   * Body-only "create new instance" UI mounted by the host (currently the instance form's
   * breadcrumb body) when the user clicks "+ Create" on a picker. Must expose:
   *
   *  - Inputs:  `initialName?: string` (optional, prefill from the picker's typed query)
   *  - Outputs: `save` and `cancel` event emitters. The shape of `save`'s payload is
   *    determined by {@link createMode}.
   *
   * The same component can also be mounted by future hosts (inline section, side panel,
   * standalone modal) — it must not assume any particular wrapper.
   */
  createView?: Type<unknown>;
  /** Persistence semantics for `createView`. See {@link SystemObjectCreateMode}. */
  createMode?: SystemObjectCreateMode;
}

/**
 * Shape of the field passed to {@link AttributeComponentRegistry.resolve}. Kept as a structural
 * subset so callers don't have to import the full FieldRendererField.
 */
export interface ResolvableField {
  type: string;
  targetDefinitionSlug?: string;
}

/**
 * Registry that maps fields to their display/input component pairs.
 *
 * Two lookup paths:
 *  - **By property type** (`'string'`, `'date'`, `'relation'`, …) — used for primitive types
 *    and generic relations. Plugins can override or extend via {@link register}.
 *  - **By target system-object slug** (`'tags'`, `'location'`, …) — used for relation/record
 *    fields that point to a known system object with a purpose-built renderer. Specialized
 *    renderers register via {@link registerForSystemObject}.
 *
 * Resolution (see {@link resolve}): when a field's `targetDefinitionSlug` matches a registered
 * system slug, the specialized pair wins. Otherwise the property-type lookup applies.
 *
 * Example:
 * ```typescript
 * // Generic per-type override
 * registry.register('deceased-info', {
 *   display: DeceasedInfoDisplayComponent,
 *   input: DeceasedInfoInputComponent,
 * });
 *
 * // Specialized renderer for a system object
 * registry.registerForSystemObject('tags', {
 *   display: TagsRelationDisplayComponent,
 *   input: TagsRelationInputComponent,
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AttributeComponentRegistry {
  private readonly byType = new Map<string, AttributeComponents>();
  private readonly bySystemSlug = new Map<string, PartialAttributeComponents>();
  private readonly bundleBySystemSlug = new Map<string, SystemObjectBundle>();

  constructor() {
    this.register('string',    { display: TextDisplayComponent,    input: TextInputComponent });
    this.register('number',    { display: NumberDisplayComponent,  input: NumberInputComponent });
    this.register('integer',   { display: NumberDisplayComponent,  input: NumberInputComponent });
    this.register('date',      { display: DateDisplayComponent,    input: DateInputComponent });
    this.register('date-time', { display: DateTimeDisplayComponent, input: DateTimeInputComponent });
    this.register('boolean',   { display: BooleanDisplayComponent, input: BooleanInputComponent });
    this.register('select',    { display: SelectDisplayComponent,  input: SelectInputComponent });
    this.register('state',     { display: SelectDisplayComponent,  input: SelectInputComponent });
    // 'relation' is the canonical key for field references to another custom object's instance.
    // 'record' is kept as a back-compat alias because schema-builder's Record attribute type
    // (a UI shortcut for many-to-one relationships) may still reach this registry with that key.
    this.register('relation', { display: RelationDisplayComponent, input: RelationInputComponent });
    this.register('record',   { display: RelationDisplayComponent, input: RelationInputComponent });
  }

  register(type: string, components: AttributeComponents): void {
    this.byType.set(type, components);
  }

  registerForSystemObject(slug: string, components: PartialAttributeComponents): void {
    this.bySystemSlug.set(slug, components);
  }

  /**
   * Register a full bundle for a system-object slug. Also writes the bundle's picker pair into
   * the legacy {@link bySystemSlug} map so callers using {@link resolve} continue to find them.
   * Prefer this over {@link registerForSystemObject} for new system objects — it carries the
   * createView + createMode that the instance-form's breadcrumb path uses.
   */
  registerBundle(slug: string, bundle: SystemObjectBundle): void {
    this.bundleBySystemSlug.set(slug, bundle);
    if (bundle.pickerInput || bundle.pickerDisplay) {
      this.bySystemSlug.set(slug, {
        display: bundle.pickerDisplay,
        input: bundle.pickerInput,
      });
    }
  }

  /**
   * Look up the bundle registered for a system-object slug, or `undefined` if none.
   * The instance form's `onCreateNewRecord` consults this to decide whether to mount a custom
   * createView in the breadcrumb body or fall through to the generic schema-driven form.
   */
  getBundle(slug: string): SystemObjectBundle | undefined {
    return this.bundleBySystemSlug.get(slug);
  }

  /**
   * Resolve display/input components for a field. Relation-like fields with a registered system
   * slug win over the generic type lookup; either half (display/input) of the system pair may be
   * absent, in which case that half falls back to the property-type map. Everything else falls
   * back to the property-type map directly (with a TextDisplay/TextInput default for unknown
   * types).
   */
  resolve(field: ResolvableField): AttributeComponents {
    const typeComponents = this.byType.get(field.type)
      ?? { display: TextDisplayComponent, input: TextInputComponent };
    if ((field.type === 'relation' || field.type === 'record') && field.targetDefinitionSlug) {
      const specialized = this.bySystemSlug.get(field.targetDefinitionSlug);
      if (specialized) {
        return {
          display: specialized.display ?? typeComponents.display,
          input: specialized.input ?? typeComponents.input,
        };
      }
    }
    return typeComponents;
  }

  /**
   * Property-type lookup. Kept for back-compat with call-sites that only know the type string.
   * New code should prefer {@link resolve} which also honours system-object specialization.
   */
  get(type: string): AttributeComponents {
    return this.byType.get(type) ?? { display: TextDisplayComponent, input: TextInputComponent };
  }
}
